/*
 * Signal — the filter bar's overflow, and its menus.
 * ---------------------------------------------------------------------------
 * Two small jobs on one row.
 *
 * OVERFLOW. The type chips are a fixed vocabulary — six on the deepest game —
 * but the row they share with a logo, a set menu and three controls is not wide
 * enough for all of them at every viewport. <signal-chipbar> measures what fits
 * and moves the remainder behind a "+N" menu, re-measuring whenever the box
 * changes size.
 *
 * The chips are authored once and this clones them into the menu, so the two
 * copies cannot drift and the Liquid has one list to maintain. Both copies then
 * exist for the life of the page and overflowing is `hidden` moving between
 * them: moving nodes between two flex contexts means measuring a layout that is
 * already mid-change, where an attribute takes the node out of the tab order
 * with it and cannot leave a chip in two places if the loop is interrupted.
 *
 * NOTHING IS CLIPPED UNTIL data-signal-ready IS SET. With scripting off the
 * strip wraps instead, which is the two-row bar this replaced — usable, just
 * taller — and never a row with chips cut off it and no way to reach them.
 *
 * That attribute is NOT set here. This is a deferred module, so waiting for it
 * meant every load painted a wrapped bar and then snapped to one row;
 * snippets/signal-chipbar.liquid claims the row from an inline script during
 * parsing instead, before the bar is painted once. connectedCallback sets it too,
 * for the case that script cannot cover: a facet change replaces this markup, and
 * a script injected as markup does not run.
 *
 * ORDER MATTERS MORE THAN THE CAP. Whatever the row cannot fit is hidden from
 * the end, so the axis is sorted with the biggest group first by the Liquid that
 * builds it. Alphabetical would have hidden Riftbound's Sleeves — sixteen of its
 * fifty-one products — while keeping Bundle at three, which is the bug the
 * mega-menu panel already hit once when it capped its chips at five.
 *
 * DISMISSAL. <details> opens, closes and takes the keyboard by itself, which is
 * why the menus are built from it. It has no opinion about an outside click or
 * Escape, so those two are added here for the whole page at once. With scripting
 * off the menus still work; they just stay open until clicked shut.
 *
 * The outside-dismiss listener is bound to `pointerdown`, not `click` — iOS
 * Safari withholds the synthetic `click` event on a tap that lands on an inert
 * area (no handler, no naturally-clickable element under the finger), so a
 * `click` listener alone left the menu unable to be dismissed by tapping past
 * it on that browser. `pointerdown` always fires. assets/signal-header.js
 * already uses this for the same reason.
 */

const MENU_SELECTOR = 'details[data-signal-setmenu]';

class SignalChipbar extends HTMLElement {
  /** @type {ResizeObserver | undefined} */
  observer;

  connectedCallback() {
    this.strip = this.querySelector('[data-signal-strip]');
    this.more = this.querySelector('[data-signal-more]');
    this.panel = this.querySelector('[data-signal-more-panel]');
    this.count = this.querySelector('[data-signal-more-count]');

    if (
      !(this.strip instanceof HTMLElement) ||
      !(this.more instanceof HTMLElement) ||
      !(this.panel instanceof HTMLElement)
    ) {
      return;
    }

    this.chips = /** @type {HTMLElement[]} */ ([...this.strip.querySelectorAll('[data-signal-chip]')]);

    // One clone per chip, index for index, so hiding chips[n] is revealing
    // spares[n] with no bookkeeping in between. Index 0 is "All" and is never
    // hidden, so its clone sits in the panel unused rather than being a special
    // case in the loop.
    this.spares = this.chips.map((chip) => {
      const spare = /** @type {HTMLElement} */ (chip.cloneNode(true));

      spare.className = 'signal-setmenu__item';
      spare.removeAttribute('data-signal-chip');
      spare.hidden = true;
      this.panel?.appendChild(spare);

      return spare;
    });

    // Usually already set by the inline script beside the element; this covers a
    // facet re-render, where the markup is injected and that script never runs.
    this.dataset.signalReady = 'true';

    // The element is re-created wholesale when <results-list> swaps the section,
    // so there is nothing to tear down and re-attach — connectedCallback is the
    // whole lifecycle. Observing the box rather than the window catches a
    // sidebar or a font swap changing the space without the viewport moving.
    this.observer = new ResizeObserver(() => this.reflow());
    this.observer.observe(this);

    this.reflow();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  reflow() {
    const { strip, more, chips, spares } = this;
    if (!strip || !more || !chips) return;

    for (const chip of chips) chip.hidden = false;
    for (const spare of spares) spare.hidden = true;
    more.hidden = true;

    // A pixel of slack: sub-pixel widths make scrollWidth exceed clientWidth by
    // fractions on a row that visually fits, which would hide a chip for nothing.
    if (strip.scrollWidth <= strip.clientWidth + 1) return;

    // Showing the trigger takes width off the row, so it goes in before the
    // measuring loop rather than after it.
    more.hidden = false;

    let hidden = 0;

    // From the end, and never the first chip: index 0 is "All", the way back to
    // the whole collection, and a row that can only fit one thing should fit
    // that one.
    for (let index = chips.length - 1; index > 0; index -= 1) {
      // The chosen chip is the other one that has to stay. It is the only thing
      // on the row saying what you are looking at, and hiding it behind a "+2"
      // leaves a page that has been filtered with nothing to show for it. Skipped
      // rather than reordered: the next chip along goes instead, so the chosen
      // one simply ends up in the slot that chip had and the merchant's order is
      // otherwise untouched.
      if (chips[index].hasAttribute('data-on')) continue;

      chips[index].hidden = true;
      if (spares[index]) spares[index].hidden = false;
      hidden += 1;

      // Written inside the loop, not after it: the count is on the trigger, and
      // "+10" is wider than "+9". Updating it afterwards would measure every
      // step against a trigger narrower than the one that ends up on the row.
      if (this.count) this.count.textContent = String(hidden);

      if (strip.scrollWidth <= strip.clientWidth + 1) break;
    }
  }
}

if (!customElements.get('signal-chipbar')) {
  customElements.define('signal-chipbar', SignalChipbar);
}

/** @param {Element | null} keep - The one menu allowed to stay open. */
function closeMenus(keep) {
  for (const menu of document.querySelectorAll(MENU_SELECTOR)) {
    if (menu !== keep) menu.removeAttribute('open');
  }
}

// Delegated, so it keeps working on menus that did not exist at load — every
// facet change replaces the collection section's markup, bar included.
document.addEventListener('pointerdown', (event) => {
  const target = event.target instanceof Element ? event.target : null;

  closeMenus(target?.closest(MENU_SELECTOR) ?? null);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const open = document.querySelector(`${MENU_SELECTOR}[open]`);
  if (!open) return;

  closeMenus(null);

  // Escape from inside the panel should leave the keyboard on the control that
  // opened it, not at the top of the document.
  const trigger = open.querySelector('summary');
  if (trigger instanceof HTMLElement) trigger.focus();
});
